import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';
import { environment } from '../../../../../environments/environment';
import { userSortModes } from '../../../../constants/user-list-filters';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { CommunityFilter } from '../../../../models/identity/community-filter.model';
import { CommunityGroup } from '../../../../models/identity/community-group.model';
import { CommunityWithRoles } from '../../../../models/identity/community-with-roles.model';
import { Community } from '../../../../models/identity/community.model';
import { Filters } from '../../../../models/identity/filters.model';
import { Organization } from '../../../../models/identity/organization.model';
import { RoleType, UserType } from '../../../../models/identity/user-type.model';
import { ResponseFromAccountActivationFollowUp, User } from '../../../../models/identity/user.model';
import { AccountsService } from '../../../../services/identity/accounts.service';
import { CommunityService } from '../../../../services/identity/community.service';
import { OrganizationService } from '../../../../services/identity/organization.service';
import { ProfileService } from '../../../../services/identity/profile.service';
import { UserTypeService } from '../../../../services/identity/user-type.service';
import { UserService } from '../../../../services/identity/user.service';

@Component({
    selector: 'app-community-members',
    templateUrl: './community-members.component.html',
    styleUrls: ['./community-members.component.scss'],
})
export class CommunityMembersComponent implements OnInit {
    public readonly memberImportFileUrl = environment.memberImportFileUrl;

    public allSelectionMode = false;
    public allUsersFollowUpModalVisible = false;
    public communities: Community[];
    public communityCompute: Community;
    public csvFile: File;
    public csvUsers: User[];
    public csvfileForm = this.fb.group({ file: [null, [Validators.required]] });
    public dialogVisible = false;
    public exportUsersAvailable = false;
    public importStep = 1;
    public importUsersAvailable = false;
    public isSendingAccountActivationFollowUpNotification: boolean;
    public loading = true;
    public manageUsersItems: MenuItem[];
    public massEditUsersAvailable = false;
    public members: User[];
    public nbUsersTotalInCommunity: number;
    public organizations: Organization[];
    public page = 1;
    public pageSize = 10;
    public roleTypes = RoleType;
    public selectedCommunityGroups: CommunityGroup[];
    public selectedCommunityId: string;
    public selectedMembers: User[] = [];
    public selectedUsersFollowUpModalVisible = false;
    public sortMode: string = userSortModes.lastName;
    public sortOrder = 1;
    public userCurentlyEditedIds: string[] = [];
    public userTypes: UserType[];

    constructor(
        protected readonly accountsService: AccountsService,
        protected readonly profileService: ProfileService,
        private readonly fb: FormBuilder,
        protected readonly userService: UserService,
        protected readonly organizationService: OrganizationService,
        protected readonly userTypeService: UserTypeService,
        protected readonly communityService: CommunityService,
        private readonly toastrHelper: ToastrHelper,
        private translate: TranslateService,
        private readonly confirmationService: ConfirmationService,
    ) {
        this.importUsersAvailable = accountsService.isAdmin()
            ? environment.features.backOfficeAdminFeaturesAvailable.importUsers
            : environment.features.backOfficeCommunityManagerFeaturesAvailable.importUsers;
        this.exportUsersAvailable = accountsService.isAdmin()
            ? environment.features.backOfficeAdminFeaturesAvailable.exportUsers
            : environment.features.backOfficeCommunityManagerFeaturesAvailable.exportUsers;
        this.massEditUsersAvailable = accountsService.isAdmin()
            ? environment.features.backOfficeAdminFeaturesAvailable.massEditUsers
            : environment.features.backOfficeCommunityManagerFeaturesAvailable.massEditUsers;
    }

    public get nextButtonLabel(): string {
        if (this.importStep === 2) {
            return "Terminer l'import";
        }
        return 'Suivant';
    }

    public get nextStepButtonDisabled(): boolean {
        switch (this.importStep) {
            case 1:
                return this.csvfileForm.invalid;
            case 2:
                return !this.csvUsers;
            default:
                return false;
        }
    }

    public addManageMenuItems() {
        this.manageUsersItems = [];

        const accountActivationFollowUpAllItem: MenuItem = {
            label: 'Relancer tous les utilisateurs',
            command: () => {
                this.showAllUsersFollowUpModalModal();
            },
        };
        this.manageUsersItems.push(accountActivationFollowUpAllItem);

        const accountActivationFollowUpSelectedItem: MenuItem = {
            label: 'Relancer les utilisateurs sélectionnés',
            command: () => {
                this.showSelectedUsersFollowUpModalModal();
            },
            disabled: this.selectedMembers.length === 0,
        };
        this.manageUsersItems.push(accountActivationFollowUpSelectedItem);

        if (this.importUsersAvailable) {
            const importUserMenuItem: MenuItem = {
                label: 'Import des utilisateurs',
                command: () => {
                    this.showDialog();
                },
            };
            this.manageUsersItems.push(importUserMenuItem);
        }
    }

    public changeCommunityInService(newCommunity: Community) {
        this.accountsService.changeCurrentCommunity(newCommunity);
    }

    public closeAllUsersFollowUpModal() {
        this.allUsersFollowUpModalVisible = false;
    }

    public closeSelectedUsersFollowUpModal() {
        this.selectedUsersFollowUpModalVisible = false;
    }

    public deleteMember(member: User) {
        this.confirmationService.confirm({
            message: this.translate.instant('community.nav.members.remove.confirmMessage', { name: `${member.firstName} ${member.lastName}` }),
            header: this.translate.instant('components.modal.confirmHeader'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: this.translate.instant('components.button.buttonYes'),
            rejectLabel: this.translate.instant('components.button.buttonCancel'),
            rejectButtonStyleClass: 'p-button-text',
            accept: () => {
                this.communityService.removeMember(this.selectedCommunityId, member.userId).subscribe({
                    next: () => {
                        this.toastrHelper.showSuccess(this.translate.instant('community.nav.members.remove.success'));
                        this.loadCommunityData();
                        this.getCommunityMembers();
                    },
                    error: (error) => {
                        this.toastrHelper.showError(this.translate.instant('community.nav.members.remove.error'));
                    },
                });
            },
        });
    }

    public exportCommunityMembersDetailsInCSV() {
        this.communityService.getSelectedCommunityMembersDetailsForCSV(this.selectedCommunityId).subscribe((response) => {
            const dataType = response.type;
            const binaryData = [];
            binaryData.push(response);
            const downloadLink = document.createElement('a');
            downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
            downloadLink.setAttribute('download', 'Membres de la communauté ' + this.communityCompute.displayName + '.csv');
            document.body.appendChild(downloadLink);
            downloadLink.click();
        });
    }

    public getCommunityMembers(userCustomSearch: string = null) {
        this.selectedMembers = [];
        this.loading = true;

        const filters: Filters = new Filters();

        filters.page = this.page;
        filters.pageSize = this.pageSize;
        filters.sortMode = this.sortMode;
        filters.sortOrder = this.sortOrder;

        filters.userCustomSearch = userCustomSearch ? userCustomSearch + '*' : '*';
        filters.communities = [new CommunityFilter(this.selectedCommunityId, [])];

        this.communityService.getUsersByCommunityId(this.selectedCommunityId, filters).subscribe((result) => {
            this.nbUsersTotalInCommunity = result.count;
            this.members = result.data;
            this.loading = false;
        });
    }

    public getOrganizationById(organizationId: string): Organization {
        return this.organizations?.find((organization) => organization.id == organizationId);
    }

    public getUserCommunitySelected(communities: CommunityWithRoles[]): CommunityWithRoles {
        return communities.filter((community) => community.id == this.selectedCommunityId)[0];
    }

    public getUserTypeLibelle(userTypeId: string): string {
        return this.userTypes?.find((userType) => userType.id == userTypeId)?.libelle;
    }

    public getUserTypesCategorized(roleType: RoleType): UserType[] {
        return this.userTypes?.filter((userType) => userType.roleType == roleType);
    }

    public handleCsvFileFormChange() {
        this.csvFile = this.csvfileForm.controls.file.value;
    }

    public handleDialogCancelButtonClick() {
        this.csvfileForm.resetFormCleanly();
        this.dialogVisible = false;
        this.importStep = 1;
    }

    public handleNextStepButtonClick() {
        switch (this.importStep) {
            case 1:
                this.importStep++;
                break;
            case 2:
                this.finishCsvImport();
                break;
            default:
                return false;
        }
    }

    public isUserCurentlyEdited(userId: string): boolean {
        return this.userCurentlyEditedIds.includes(userId);
    }

    public isUserPrimaryCommunityId(userPrimaryCommunityId: string): boolean {
        return this.selectedCommunityId == userPrimaryCommunityId;
    }

    public loadCommunityData() {
        this.selectedCommunityGroups = this.accountsService.currentCommunity.communitiesGroups;
        this.communityService.getCommunityById(this.selectedCommunityId).subscribe((data) => {
            this.communityCompute = data;
        });
    }

    public loadData() {
        this.userTypeService.getUserTypes().subscribe((userTypes) => {
            this.userTypes = userTypes;
        });
    }

    public loadMembersLazy(event: LazyLoadEvent) {
        this.pageSize = event.rows;
        this.page = event.first / this.pageSize + 1;
        this.sortMode = event.sortField ? event.sortField : userSortModes.lastName;
        this.sortOrder = event.sortOrder;
        this.getCommunityMembers(event.globalFilter);
    }

    public ngOnInit(): void {
        if (this.accountsService.isAdmin()) {
            this.communityService.getCommunities().subscribe((communities) => {
                this.communities = communities;
            });
        } else {
            this.communities = this.accountsService.getCommunitiesManagedByUser();
        }

        this.selectedCommunityId = this.accountsService.currentCommunity.id;
        this.loadData();
        this.loadCommunityData();

        this.accountsService.currentCommunityChange.subscribe(() => {
            this.selectedCommunityId = this.accountsService.currentCommunity.id;
            this.loadCommunityData();
            this.page = 1;
            this.getCommunityMembers();
        });
        this.addManageMenuItems();
        this.getCommunityMembers(null);
    }

    public onAllSelectionClick() {
        this.allSelectionMode = !this.allSelectionMode;
        if (!this.allSelectionMode) {
            this.selectedMembers = [];
        }
    }

    public onChangeCommunity(event: string) {
        const community = this.communities.find((community) => community.id == event);

        if (community != null) {
            this.changeCommunityInService(community);
        }
    }

    public onHeaderCheckboxToggle() {
        if (this.allSelectionMode) {
            this.allSelectionMode = !this.allSelectionMode;
            this.selectedMembers = [];
        }
    }

    public onRowEditCancel(member: User, index: number) {
        this.userCurentlyEditedIds = this.userCurentlyEditedIds.filter((id) => id != member.userId);
    }

    public onRowEditInit(member: User) {
        this.userCurentlyEditedIds.push(member.userId);
    }

    public async onRowEditSave(member: User) {
        this.userService.updateUser({ ...member, pictureHasChanged: false }).subscribe(() => {
            this.toastrHelper.showSuccess(this.translate.instant('toastMessages.userUpdateSuccess'));
        });
        this.userCurentlyEditedIds = this.userCurentlyEditedIds.filter((id) => id != member.userId);
    }

    public sendAccountActivationFollowUpNotification(sendToAll: boolean) {
        if (this.isSendingAccountActivationFollowUpNotification) return;

        this.isSendingAccountActivationFollowUpNotification = true;

        let selectedMembersIds: string[] = [];

        if (!sendToAll) {
            selectedMembersIds = this.selectedMembers.map((member) => member.userId);
        }

        this.communityService.sendAccountActivationFollowUpNotification(this.selectedCommunityId, selectedMembersIds).subscribe(
            (response: ResponseFromAccountActivationFollowUp) => {
                const blob = new Blob(['Succès : \n', response.success, '\n', 'Echecs : \n', response.errors], {
                    type: 'text/plain',
                });
                const now = new Date();
                const fileName = `Relance pour activation de la communauté ${this.accountsService.currentCommunity.displayName} du ${now.getDate()}-${
                    now.getMonth() + 1
                }-${now.getFullYear()}.txt`;

                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(blob);
                downloadLink.setAttribute('download', fileName);
                document.body.appendChild(downloadLink);
                downloadLink.click();

                const message = this.translate.instant('community.pages.members.toast.accountActivationFollowUpMails');
                this.toastrHelper.showSuccess(message);

                if ((response.errors || []).length > 0) {
                    const warning = this.translate.instant('community.pages.members.toast.accountActivationFollowUpMailsSomeErrors');
                    this.toastrHelper.showWarning(warning);
                }

                this.isSendingAccountActivationFollowUpNotification = false;
            },
            () => {
                const message = this.translate.instant('common.unknownError');
                this.toastrHelper.showError(message);
            },
        );
    }

    public showAllUsersFollowUpModalModal() {
        this.allUsersFollowUpModalVisible = true;
    }

    public showDialog() {
        this.dialogVisible = true;
    }

    public showSelectedUsersFollowUpModalModal() {
        this.selectedUsersFollowUpModalVisible = true;
    }

    public updateCsvUsers(users: User[]) {
        this.csvUsers = users;
    }

    private finishCsvImport() {
        return;
    }
}
